// src/components/wells/WellAiStatusCell.js
import React from 'react';
// --- UPDATE: memoisation hooks + emotion keyframes (Chakra peer dependency; not re-exported by every Chakra version)
import { memo, useMemo, useEffect, useState } from 'react';
import { keyframes } from '@emotion/react';
// --- END UPDATE
import {
  VStack,
  HStack,
  Badge,
  Text,
  Tooltip,
  Progress,
  Box,
  Icon,
  useColorModeValue,
  // --- UPDATE
  IconButton,
  CircularProgress,
  Divider,
  // --- END UPDATE
} from '@chakra-ui/react';
import { MdShield, MdAccessTime, MdSpeed, MdAutoAwesome } from 'react-icons/md';
// --- UPDATE
import {
  MdTrendingUp,
  MdTrendingDown,
  MdTrendingFlat,
  MdGrain,
  MdOpenInNew,
  MdUpdate,
} from 'react-icons/md';
// --- END UPDATE

// --- UPDATE: shared AI / weather helpers — exported for WellAiSummaryCard and WellManagementPanel
/**
 * Version-independent replacement for Chakra's usePrefersReducedMotion.
 * Respects the OS "reduce motion" setting and updates live if it changes.
 */
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
export const usePrefersReducedMotion = () => {
  const [prefersReduced, setPrefersReduced] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(REDUCED_MOTION_QUERY).matches
      : false
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (e) => setPrefersReduced(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  return prefersReduced;
};

/** Predictions older than this are flagged as stale in the UI. */
export const STALE_PREDICTION_MS = 30 * 60 * 1000;

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Normalises a single 0–1 or 0–100 value into the 0–1 range. */
const toUnitInterval = (value) => {
  const n = toFiniteNumber(value);
  if (n === null) return null;
  const unit = n > 1 ? n / 100 : n;
  return Math.min(1, Math.max(0, unit));
};

/** Accepts Date, ISO string, epoch ms, epoch seconds or numeric strings. */
const parseTimestampMs = (ts) => {
  if (ts === null || ts === undefined || ts === '') return null;
  if (ts instanceof Date) return Number.isFinite(ts.getTime()) ? ts.getTime() : null;
  const numeric = Number(ts);
  if (Number.isFinite(numeric)) return numeric < 1e12 ? numeric * 1000 : numeric;
  const parsed = Date.parse(ts);
  return Number.isFinite(parsed) ? parsed : null;
};

const humanizeLabel = (label) => {
  const text = String(label ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Factor';
};

/** Same lookup order the components already use: dictionary first, then enriched well object. */
export const getAiForWell = (well, aiPredictions = {}) => {
  if (!well) return null;
  return (aiPredictions && aiPredictions[String(well.id)]) || well.ai_predictions || null;
};

/** Forecast series → 0–1 numbers. Accepts numbers or {p|probability|prob|value}; 0–100 series auto-scaled. */
const normalizeSeries = (raw) => {
  if (!Array.isArray(raw)) return [];
  const values = raw
    .map((item) =>
      toFiniteNumber(
        item !== null && typeof item === 'object'
          ? item.p ?? item.probability ?? item.prob ?? item.value
          : item
      )
    )
    .filter((v) => v !== null);
  const scale = values.some((v) => v > 1) ? 100 : 1;
  return values.map((v) => Math.min(1, Math.max(0, v / scale))).slice(-48);
};

/** Model drivers → [{label, weight, share}] sorted by absolute weight. Accepts arrays, strings or {feature: weight} maps. */
const normalizeFactors = (raw) => {
  if (!raw) return [];
  let list = [];
  if (Array.isArray(raw)) {
    list = raw
      .map((item) => {
        if (typeof item === 'string') return { label: item, weight: null };
        if (item && typeof item === 'object') {
          return {
            label: item.label ?? item.name ?? item.feature ?? item.factor ?? 'Factor',
            weight: toFiniteNumber(
              item.weight ?? item.importance ?? item.impact ?? item.contribution ?? item.value
            ),
          };
        }
        return null;
      })
      .filter(Boolean);
  } else if (typeof raw === 'object') {
    list = Object.entries(raw).map(([label, weight]) => ({ label, weight: toFiniteNumber(weight) }));
  } else if (typeof raw === 'string') {
    list = raw
      .split(/[,;]/)
      .map((s) => ({ label: s.trim(), weight: null }))
      .filter((f) => f.label);
  }
  const maxWeight = list.reduce((max, f) => Math.max(max, Math.abs(f.weight ?? 0)), 0);
  return list
    .map((f) => ({
      label: humanizeLabel(f.label),
      weight: f.weight,
      share: maxWeight > 0 && f.weight !== null ? Math.abs(f.weight) / maxWeight : null,
    }))
    .sort((a, b) => Math.abs(b.weight ?? 0) - Math.abs(a.weight ?? 0))
    .slice(0, 6);
};

const normalizeRecommendations = (raw) => {
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list
    .map((item) =>
      typeof item === 'string'
        ? item.trim()
        : item && typeof item === 'object'
        ? String(item.action ?? item.text ?? item.title ?? item.description ?? '').trim()
        : ''
    )
    .filter(Boolean)
    .slice(0, 5);
};

/**
 * Normalises every AI field the UI understands (camelCase and snake_case payloads).
 * Core fields reproduce the existing component fallbacks exactly.
 */
export const extractAiMetrics = (ai) => {
  if (!ai) return null;
  const probability = toFiniteNumber(ai.floodRiskProbability ?? ai.flood_probability) ?? 0;
  const probabilityPct = Math.round(probability * 100);
  const severity = String(ai.predictedSeverity || ai.flood_risk || 'NONE').toUpperCase();
  const estMinutes = toFiniteNumber(ai.estimatedMinutesToOvertopping ?? ai.est_minutes_to_overtopping);
  const rateOfRise = toFiniteNumber(ai.rateOfRisePerMinute ?? ai.rate_of_rise_per_minute) ?? 0;
  // --- UPDATE (wells v1): no invented values — health / RUL stay null unless the model returned them
  const healthScore = toFiniteNumber(ai.health_score);
  const rulDays = toFiniteNumber(ai.predicted_rul_days ?? ai.rul);
  // --- END UPDATE
  const confidence = toUnitInterval(
    ai.confidence ?? ai.model_confidence ?? ai.confidence_score ?? ai.confidenceScore
  );
  const previous = toFiniteNumber(
    ai.previous_probability ?? ai.previousFloodRiskProbability ?? ai.prev_flood_probability
  );
  const probabilityDelta = previous !== null ? probability - previous : null;

  let trend = String(ai.trend ?? ai.risk_trend ?? ai.riskTrend ?? '').toLowerCase();
  if (!['rising', 'falling', 'stable'].includes(trend)) {
    if (probabilityDelta !== null) {
      trend = probabilityDelta > 0.02 ? 'rising' : probabilityDelta < -0.02 ? 'falling' : 'stable';
    } else if (rateOfRise > 0) trend = 'rising';
    else if (rateOfRise < 0) trend = 'falling';
    else trend = null;
  }

  return {
    probability,
    probabilityPct,
    severity,
    estMinutes,
    rateOfRise,
    healthScore,
    rulDays,
    confidence,
    probabilityDelta,
    trend,
    forecast: normalizeSeries(
      ai.probability_forecast ?? ai.probabilityForecast ?? ai.flood_forecast ?? ai.forecast
    ),
    factors: normalizeFactors(
      ai.top_factors ?? ai.topFactors ?? ai.contributing_factors ?? ai.feature_importance ?? ai.drivers
    ),
    recommendations: normalizeRecommendations(
      ai.recommendations ?? ai.recommended_actions ?? ai.recommended_action ?? ai.recommendation
    ),
    modelVersion: ai.model_version ?? ai.modelVersion ?? null,
    updatedAt: parseTimestampMs(
      ai.updated_at ?? ai.updatedAt ?? ai.generated_at ?? ai.predicted_at ?? ai.timestamp
    ),
    rainfall24hMm: toFiniteNumber(ai.rainfall_mm_24h ?? ai.precipitation_24h ?? ai.weather?.rain_24h),
  };
};

/** Risk tiers — same thresholds as getSeverityScheme in the cell below (red/orange/yellow/green). */
export const AI_RISK_TIERS = Object.freeze({
  critical: { key: 'critical', label: 'Critical', scheme: 'red', color: 'red.500', rank: 3 },
  high: { key: 'high', label: 'High', scheme: 'orange', color: 'orange.400', rank: 2 },
  watch: { key: 'watch', label: 'Watch', scheme: 'yellow', color: 'yellow.500', rank: 1 },
  stable: { key: 'stable', label: 'Stable', scheme: 'green', color: 'green.500', rank: 0 },
});

export const getAiRiskTier = (metrics) => {
  if (!metrics) return null;
  const { severity: sev, probabilityPct: prob } = metrics;
  if (sev === 'CRITICAL' || prob >= 85) return 'critical';
  if (sev === 'HIGH' || prob >= 65) return 'high';
  if (sev === 'WARNING' || prob >= 40) return 'watch';
  return 'stable';
};

export const formatMinutesShort = (mins) => {
  const m = toFiniteNumber(mins);
  if (m === null) return 'N/A';
  if (m <= 0) return 'Now';
  if (m < 60) return `~${Math.round(m)}m`;
  if (m < 1440) {
    const h = Math.floor(m / 60);
    const rem = Math.round(m % 60);
    return rem ? `~${h}h ${rem}m` : `~${h}h`;
  }
  return `~${Math.round(m / 1440)}d`;
};

export const formatRelativeTime = (timestampMs, nowMs = Date.now()) => {
  if (!Number.isFinite(timestampMs)) return null;
  const diffSec = Math.max(0, Math.round((nowMs - timestampMs) / 1000));
  if (diffSec < 45) return 'just now';
  const mins = Math.round(diffSec / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

/** Rainfall-accumulation risk bands for 24h totals (mm). */
export const getRainfallRisk = (mm24) => {
  const v = toFiniteNumber(mm24);
  if (v === null) return { level: 'UNKNOWN', scheme: 'gray', rank: 0 };
  if (v >= 50) return { level: 'SEVERE', scheme: 'red', rank: 4 };
  if (v >= 25) return { level: 'HIGH', scheme: 'orange', rank: 3 };
  if (v >= 10) return { level: 'MODERATE', scheme: 'yellow', rank: 2 };
  return { level: 'LOW', scheme: 'green', rank: 1 };
};

export const aiCriticalPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.55); }
  70% { box-shadow: 0 0 0 6px rgba(229, 62, 62, 0); }
  100% { box-shadow: 0 0 0 0 rgba(229, 62, 62, 0); }
`;

const evaluatingShimmer = keyframes`
  0% { background-position: -120px 0; }
  100% { background-position: 120px 0; }
`;

const TREND_META = Object.freeze({
  rising: { icon: MdTrendingUp, color: 'red.400', label: 'Risk rising' },
  falling: { icon: MdTrendingDown, color: 'green.400', label: 'Risk easing' },
  stable: { icon: MdTrendingFlat, color: 'gray.400', label: 'Risk stable' },
});

export const getTrendMeta = (trend) => (trend ? TREND_META[trend] || null : null);

/** Tiny dependency-free SVG sparkline (0–1 values). Colour follows `currentColor`. */
export const AiSparkline = memo(
  ({ values = [], width = 90, height = 22, threshold = null, strokeWidth = 1.5, ariaLabel }) => {
    const geometry = useMemo(() => {
      if (!Array.isArray(values) || values.length < 2) return null;
      const pad = 2;
      const w = width - pad * 2;
      const h = height - pad * 2;
      const step = w / (values.length - 1);
      const pts = values.map((v, i) => [pad + i * step, pad + (1 - Math.min(1, Math.max(0, v))) * h]);
      const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
      const last = pts[pts.length - 1];
      const area = `${line} L${last[0].toFixed(1)},${height - pad} L${pts[0][0].toFixed(1)},${height - pad} Z`;
      const thresholdY = threshold !== null ? pad + (1 - threshold) * h : null;
      return { line, area, thresholdY, last };
    }, [values, width, height, threshold]);

    if (!geometry) return null;

    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel || 'Flood probability trend'}
        style={{ display: 'block', overflow: 'visible' }}
      >
        <path d={geometry.area} fill="currentColor" opacity={0.15} />
        {geometry.thresholdY !== null && (
          <line
            x1={0}
            x2={width}
            y1={geometry.thresholdY}
            y2={geometry.thresholdY}
            stroke="currentColor"
            strokeOpacity={0.4}
            strokeDasharray="2 2"
            strokeWidth={1}
          />
        )}
        <path
          d={geometry.line}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx={geometry.last[0]} cy={geometry.last[1]} r={2} fill="currentColor" />
      </svg>
    );
  }
);
AiSparkline.displayName = 'AiSparkline';
// --- END UPDATE

export const WellAiStatusCell = ({
  well,
  aiPredictions = {},
  subtextColor,
  // --- UPDATE: optional props (backwards compatible)
  weather = null,
  onOpenInsights,
  staleAfterMs = STALE_PREDICTION_MS,
  // --- END UPDATE
}) => {
  // --- UPDATE: hooks hoisted above the early return (Rules of Hooks)
  const prefersReducedMotion = usePrefersReducedMotion();
  const shimmerBg = useColorModeValue(
    'linear-gradient(90deg, rgba(237,242,247,0) 0%, rgba(214,188,250,0.9) 50%, rgba(237,242,247,0) 100%)',
    'linear-gradient(90deg, rgba(45,55,72,0) 0%, rgba(128,90,213,0.55) 50%, rgba(45,55,72,0) 100%)'
  );
  const rainChipColor = useColorModeValue('blue.600', 'blue.200');
  const resolvedAi = getAiForWell(well, aiPredictions);
  const metrics = useMemo(() => extractAiMetrics(resolvedAi), [resolvedAi]);
  // --- END UPDATE

  // Support both dictionary lookup and direct object enrichment
  const ai = aiPredictions[String(well.id)] || well.ai_predictions;

  // --- UPDATE (wells v1): without a flood prediction, show what Well Intelligence knows instead of an
  // endless "EVALUATING…" (wells with no sensor never get a flood prediction)
  if (!ai && well?.intel) {
    const it = well.intel;
    const scheme = { critical: 'red', high: 'orange', watch: 'yellow', stable: 'green' }[it.tier] || 'gray';
    return (
      <Tooltip label={`${it.summary}${it.data_gaps?.length ? ` — ${it.data_gaps[0]}` : ''}`} hasArrow placement="top">
        <Badge colorScheme={scheme} variant="subtle" fontSize="9px" borderRadius="full" px={2} py={0.5} display="inline-flex" alignItems="center" gap={1}>
          <Icon as={MdAutoAwesome} boxSize={2.5} />
          {it.tier === 'unknown' ? 'NOT MONITORED' : `RISK ${it.score}`}
        </Badge>
      </Tooltip>
    );
  }
  // --- END UPDATE
  if (!ai) {
    return (
      <Badge
        colorScheme="gray"
        variant="subtle"
        fontSize="9px"
        borderRadius="full"
        px={2}
        py={0.5}
        // --- UPDATE: AI "thinking" shimmer while the model evaluates
        bgImage={prefersReducedMotion ? undefined : shimmerBg}
        bgRepeat="no-repeat"
        bgSize="120px 100%"
        animation={prefersReducedMotion ? undefined : `${evaluatingShimmer} 1.6s linear infinite`}
        display="inline-flex"
        alignItems="center"
        gap={1}
        aria-busy="true"
        // --- END UPDATE
      >
        {/* --- UPDATE */}
        <Icon as={MdAutoAwesome} boxSize={2.5} />
        {/* --- END UPDATE */}
        EVALUATING...
      </Badge>
    );
  }

  const floodProb = Math.round((ai.floodRiskProbability ?? ai.flood_probability ?? 0) * 100);
  const severity = (ai.predictedSeverity || ai.flood_risk || 'NONE').toUpperCase();
  const estMins = ai.estimatedMinutesToOvertopping ?? ai.est_minutes_to_overtopping;
  const rateOfRise = ai.rateOfRisePerMinute ?? ai.rate_of_rise_per_minute ?? 0;
  // --- UPDATE (wells v1): null when the model has no health / RUL (was 100 − flood% and 365 days)
  const healthScore = toFiniteNumber(ai.health_score);
  const rulDays = toFiniteNumber(ai.predicted_rul_days ?? ai.rul);
  // --- END UPDATE

  const getSeverityScheme = (sev, prob) => {
    if (sev === 'CRITICAL' || prob >= 85) return 'red';
    if (sev === 'HIGH' || prob >= 65) return 'orange';
    if (sev === 'WARNING' || prob >= 40) return 'yellow';
    return 'green';
  };

  const badgeScheme = getSeverityScheme(severity, floodProb);

  const tooltipLabel = [
    `AI Flood Probability: ${floodProb}% (${severity})`,
    estMins !== null && estMins !== undefined
      ? estMins === 0
        ? 'Status: Overtopping now!'
        : `Est. time to overtop: ~${estMins} minutes`
      : null,
    rateOfRise > 0 ? `Water rise velocity: ${(rateOfRise).toFixed(3)}%/min` : null,
    healthScore !== null ? `Health Score: ${healthScore}%` : null,
    rulDays !== null ? `RUL: ${rulDays} days` : null,
  ].filter(Boolean).join(' • ');

  // --- UPDATE: derived AI + weather context
  const tierKey = getAiRiskTier(metrics) || 'stable';
  const tierMeta = AI_RISK_TIERS[tierKey];
  const trendMeta = getTrendMeta(metrics?.trend);
  const isStale =
    metrics?.updatedAt !== null &&
    metrics?.updatedAt !== undefined &&
    Date.now() - metrics.updatedAt > staleAfterMs;
  const rainNext6 =
    weather && Number.isFinite(Number(weather.next6hRainMm)) ? Number(weather.next6hRainMm) : null;
  const rainAmplified = rainNext6 !== null && rainNext6 >= 5 && floodProb >= 40;
  const confidencePct =
    metrics?.confidence !== null && metrics?.confidence !== undefined
      ? Math.round(metrics.confidence * 100)
      : null;
  const delta = metrics?.probabilityDelta ?? null;

  const richTooltip = (
    <Box maxW="300px" py={1}>
      <HStack spacing={1} mb={1}>
        <Icon as={MdAutoAwesome} boxSize={3} />
        <Text fontSize="xs" fontWeight="800">
          FlowSense AI · {tierMeta.label} risk
        </Text>
      </HStack>
      <Text fontSize="2xs" lineHeight="1.5">
        {tooltipLabel}
      </Text>
      {confidencePct !== null && (
        <Text fontSize="2xs" mt={1}>
          Model confidence: {confidencePct}%
        </Text>
      )}
      {delta !== null && (
        <Text fontSize="2xs">
          Change vs previous run: {delta > 0 ? '+' : ''}
          {Math.round(delta * 100)} pts
        </Text>
      )}
      {metrics?.factors?.length > 0 && (
        <>
          <Divider my={1.5} opacity={0.4} />
          <Text fontSize="2xs" fontWeight="800" mb={0.5}>
            Top model drivers
          </Text>
          {metrics.factors.slice(0, 3).map((f) => (
            <Text key={f.label} fontSize="2xs">
              • {f.label}
            </Text>
          ))}
        </>
      )}
      {rainNext6 !== null && (
        <Text fontSize="2xs" mt={1}>
          Forecast rainfall (next 6h): {rainNext6} mm
          {rainAmplified ? ' — rain-amplified risk' : ''}
        </Text>
      )}
      {metrics?.recommendations?.[0] && (
        <>
          <Divider my={1.5} opacity={0.4} />
          <Text fontSize="2xs" fontWeight="700">
            Recommended: {metrics.recommendations[0]}
          </Text>
        </>
      )}
      {(metrics?.modelVersion || metrics?.updatedAt) && (
        <Text fontSize="9px" opacity={0.75} mt={1}>
          {metrics.modelVersion ? `Model ${metrics.modelVersion}` : ''}
          {metrics.modelVersion && metrics.updatedAt ? ' · ' : ''}
          {metrics.updatedAt ? `Updated ${formatRelativeTime(metrics.updatedAt)}` : ''}
          {isStale ? ' (stale)' : ''}
        </Text>
      )}
    </Box>
  );
  // --- END UPDATE

  return (
    // --- UPDATE: rich AI tooltip (original text kept as the accessible label)
    <Tooltip label={richTooltip} aria-label={tooltipLabel} hasArrow placement="top" openDelay={250}>
    {/* --- END UPDATE */}
      <VStack align="start" spacing={1.5} cursor="default">
        {/* Risk Badge with Probability */}
        <HStack spacing={1}>
          <Badge
            colorScheme={badgeScheme}
            variant="solid"
            fontSize="9px"
            fontWeight="800"
            borderRadius="full"
            px={2}
            py={0.5}
            display="flex"
            alignItems="center"
            gap={1}
            // --- UPDATE
            animation={
              badgeScheme === 'red' && !prefersReducedMotion
                ? `${aiCriticalPulse} 1.8s ease-out infinite`
                : undefined
            }
            opacity={isStale ? 0.65 : 1}
            // --- END UPDATE
          >
            <Icon as={MdAutoAwesome} boxSize={2.5} />
            {severity}: {floodProb}%
          </Badge>

          {estMins !== null && estMins !== undefined && (
            <HStack spacing={0.5} color={badgeScheme === 'red' ? 'red.500' : 'gray.500'}>
              <Icon as={MdAccessTime} boxSize={2.5} />
              <Text fontSize="9px" fontWeight="bold">
                {estMins === 0 ? 'SPILL NOW' : `~${estMins}m`}
              </Text>
            </HStack>
          )}

          {/* --- UPDATE: trend, confidence, staleness, drill-down */}
          {trendMeta && (
            <Icon
              as={trendMeta.icon}
              boxSize={3.5}
              color={trendMeta.color}
              aria-label={trendMeta.label}
            />
          )}
          {confidencePct !== null && (
            <CircularProgress
              value={confidencePct}
              size="14px"
              thickness="14px"
              color="purple.400"
              trackColor="blackAlpha.100"
              aria-label={`Model confidence ${confidencePct}%`}
            />
          )}
          {isStale && <Icon as={MdUpdate} boxSize={3} color="orange.400" aria-label="Stale prediction" />}
          {onOpenInsights && (
            <IconButton
              size="xs"
              h="16px"
              minW="16px"
              variant="ghost"
              colorScheme="purple"
              icon={<Icon as={MdOpenInNew} boxSize={3} />}
              aria-label={`Open AI analysis for ${well?.name || well?.id}`}
              onClick={() => onOpenInsights(well, ai)}
            />
          )}
          {/* --- END UPDATE */}
        </HStack>

        {/* --- UPDATE: forecast sparkline + rainfall amplification chip */}
        {metrics?.forecast?.length > 1 && (
          <Box color={tierMeta.color} lineHeight={0}>
            <AiSparkline
              values={metrics.forecast}
              width={90}
              height={16}
              threshold={0.85}
              ariaLabel={`Forecast flood probability for ${well?.name || well?.id}`}
            />
          </Box>
        )}
        {rainAmplified && (
          <HStack spacing={0.5} color={rainChipColor}>
            <Icon as={MdGrain} boxSize={2.5} />
            <Text fontSize="8px" fontWeight="800" letterSpacing="0.03em">
              RAIN-AMPLIFIED · {rainNext6}mm/6h
            </Text>
          </HStack>
        )}
        {/* --- END UPDATE */}

        {/* Health Score Mini Progress Bar & RUL Days */}
        {/* --- UPDATE (wells v1): only when the asset model returned a health score */}
        {healthScore !== null && (
        <Box w="90px">
          <HStack justify="space-between" mb={0.5}>
            <Text fontSize="8px" fontWeight="700" color={subtextColor}>
              HEALTH
            </Text>
            <Text fontSize="8px" fontWeight="800" color={healthScore < 50 ? 'red.400' : 'teal.500'}>
              {healthScore}%
            </Text>
          </HStack>
          <Progress
            value={healthScore}
            size="xs"
            colorScheme={healthScore > 70 ? 'teal' : healthScore > 40 ? 'orange' : 'red'}
            borderRadius="full"
          />
          <Text fontSize="8px" color={subtextColor} mt={0.5}>
            RUL: <strong style={{ color: healthScore < 50 ? '#E53E3E' : '#319795' }}>{rulDays !== null ? `${rulDays}d` : '—'}</strong>
          </Text>
        </Box>
        )}
        {/* --- END UPDATE */}
      </VStack>
    </Tooltip>
  );
};

// --- UPDATE: memoised default export — a row only re-renders when its own prediction changes
const areStatusCellPropsEqual = (prev, next) =>
  String(prev.well?.id) === String(next.well?.id) &&
  getAiForWell(prev.well, prev.aiPredictions) === getAiForWell(next.well, next.aiPredictions) &&
  prev.well?.intel === next.well?.intel && // --- UPDATE (wells v1)
  prev.subtextColor === next.subtextColor &&
  prev.weather === next.weather &&
  prev.onOpenInsights === next.onOpenInsights &&
  prev.staleAfterMs === next.staleAfterMs;

export default memo(WellAiStatusCell, areStatusCellPropsEqual);
// --- END UPDATE